import React from "react";
import styled from "styled-components";
import { Play, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { tmdbApi } from "../../services/tmdb";
import { LoadingSpinner } from "../common/LoadingSpinner";

const StreamingSection = styled.section`
  margin: var(--spacing-4) 0;
`;

const Title = styled.h3`
  font-size: 1.2rem;
  margin-bottom: var(--spacing-3);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--spacing-3);
`;

const ServiceCard = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3);
  background: var(--color-background-secondary);
  border-radius: var(--border-radius);
  text-decoration: none;
  color: var(--color-text-primary);
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    background: var(--color-background-tertiary);
  }
`;

const ServiceLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-sm);
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.h4`
  font-size: 1rem;
  margin: 0;
`;

const ServicePrice = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin: 0;
`;

interface StreamingServicesProps {
  id: number;
  type: 'movie' | 'tv';
}

export const StreamingServices: React.FC<StreamingServicesProps> = ({ id, type }) => {
  const { data: providers, isLoading } = useQuery({
    queryKey: ["providers", id, type],
    queryFn: () => tmdbApi.getStreamingProviders(id, type),
    enabled: !!id,
  });

  if (isLoading) return <LoadingSpinner />;
  if (!providers?.results?.FR) return null;

  const { flatrate = [], rent = [], buy = [] } = providers.results.FR;
  const allProviders = [...flatrate, ...rent, ...buy];

  if (allProviders.length === 0) return null;

  return (
    <StreamingSection>
      <Title>
        <Play size={20} />
        Available on Streaming
      </Title>
      <ServicesGrid>
        {allProviders.map((provider) => (
          <ServiceCard
            key={provider.provider_id}
            href={providers.results.FR.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ServiceLogo
              src={`https://image.tmdb.org/t/p/original${provider.logo_path}`}
              alt={provider.provider_name}
            />
            <ServiceInfo>
              <ServiceName>{provider.provider_name}</ServiceName>
              <ServicePrice>
                {flatrate.includes(provider)
                  ? "Subscription"
                  : rent.includes(provider)
                  ? "Rent"
                  : "Buy"}
              </ServicePrice>
            </ServiceInfo>
            <ExternalLink size={16} />
          </ServiceCard>
        ))}
      </ServicesGrid>
    </StreamingSection>
  );
};
